/**
 * Display formatters. Keep them in one place so we don't end up with three
 * different "5 minutes ago" implementations across the codebase.
 *
 * Model- or domain-specific formatters (e.g. formatAddress, formatClaimType)
 * stay alongside their types so they evolve together.
 */

export function formatCurrency(
  amount: number | null | undefined,
  options?: Intl.NumberFormatOptions
): string {
  if (amount === null || amount === undefined) {
    return '$0.00'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(amount)
}

/** Pass to formatCurrency for whole dollars, e.g. "$35,490". */
export const WHOLE_DOLLARS: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

/** Whole dollars, e.g. "$35,490 – $63,092". */
export function formatCurrencyRange({ low, high }: { low: number; high: number }): string {
  return `${formatCurrency(low, WHOLE_DOLLARS)} – ${formatCurrency(high, WHOLE_DOLLARS)}`
}

/**
 * Coerce a date string or Date to a Date, parsing YYYY-MM-DD strings in local
 * time to avoid the UTC-midnight shift that makes "1971-04-06" render as
 * April 5 west of UTC. Other strings fall through to the platform parser.
 */
export function parseDateOnly(value: string | Date | null | undefined): Date | undefined {
  if (!value) return undefined
  if (value instanceof Date) return value
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(value + 'T00:00:00')
  return new Date(value)
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return parseDateOnly(dateStr)!.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "Sep 18, 12:28 PM" — the year appears only when it isn't the current one. */
export function formatDateTimeShort(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString(undefined, {
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * The viewer's IANA timezone (e.g. "America/Los_Angeles"), falling back to
 * "UTC" when the platform can't resolve one.
 */
export function resolveTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function formatRelativeDate(dateStr: string | null | undefined, fallback: string = '-'): string {
  if (!dateStr) return fallback
  const date = new Date(dateStr)
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return '1d ago'
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return `${Math.floor(diffDays / 30)}mo ago`
}

export function formatDateForApi(value: string | undefined | null): string | null {
  // Parse via parseDateOnly so a bare YYYY-MM-DD is read as *local* midnight,
  // not UTC midnight. Reading getFullYear()/getMonth()/getDate() off a UTC
  // instant rolls the date back a day west of UTC ("1961-05-10" -> "1961-05-09").
  const date = parseDateOnly(value)
  if (!date || isNaN(date.getTime())) return null
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a US phone number for display: "+14155552671" and "4155552671" both
 * become "(415) 555-2671". Anything else is returned as-is.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  // User.mobile is stored digits-only when it came from the DOT lookup rather than the phone input.
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
  }
  return phone
}

/**
 * Format a duration in seconds as "Xs", "Xm", "Xm YYs", or null for
 * zero/negative. Used for call duration in the activity timeline.
 */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null
  // Rounded first: a fractional duration otherwise renders every digit of the
  // float, e.g. "1m 0.02556200000000075s".
  const whole = Math.round(seconds)
  const m = Math.floor(whole / 60)
  const s = whole % 60
  if (m === 0) return `${s}s`
  if (s === 0) return `${m}m`
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

/** A span of time as its two largest units: "45s", "14m 05s", "3h 05m", "2d 4h". */
export function formatTimeSpan(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return `${days}d ${hours % 24}h`
  if (hours > 0) return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
  return `${seconds}s`
}

/** "DOT number, business type and a CA number" — one line, not one row each. */
export function listSentence(items: string[]): string {
  if (items.length < 2) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
